import { useParams, useLocation, Navigate, Outlet } from "react-router-dom";
import { useContext, useMemo } from "react";
import {
  TabType,
  TabRoutes,
  NetworkingMapRoute,
} from "../../../Enums/EventEnums";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";
import {
  EventDetailDataProvider,
} from "../../../hooks/Events/EventDetailDataContext";
import EventDetailsLayout from "./EventDetailsLayout";
import AuthContext from "../../../store/AuthContext";

export default function CommonEvent() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const event = useEventLoader(id);
  const { loggedPerson } = useContext(AuthContext);

  const preloadRegistration = useMemo(() => {
    if (!event?.id || !loggedPerson?.id) {
      return null;
    }

    const hasRegistrationTab = (event.tabs ?? []).some((tab) => {
      const tabType = typeof tab === "string" ? tab : tab.type;
      return tabType === "registration";
    });

    if (!hasRegistrationTab) {
      return null;
    }

    return { personId: loggedPerson.id };
  }, [event?.id, event?.tabs, loggedPerson?.id]);

  const getActiveTabFromUrl = (): TabType | null => {
    const pathname = location.pathname;

    if (pathname.includes("/registration")) {
      return "registration";
    }

    const eventPathPattern = /^\/events\/[^/]+\/([^/]+)/;
    const match = pathname.match(eventPathPattern);

    if (match && match[1]) {
      const routeSegment = match[1];
      const routeToTabType = (route: string): TabType | null => {
        const entry = Object.entries(TabRoutes).find(
          ([_, routeValue]) => routeValue === route
        );
        return entry ? (entry[0] as TabType) : null;
      };
      return routeToTabType(routeSegment);
    }

    return null;
  };

  if (location.pathname.endsWith(`/${NetworkingMapRoute}`)) {
    return <Outlet />;
  }

  if (!event) {
    return null;
  }

  const sortedTabs = event.tabs || [];

  const getTabType = (
    tab: TabType | { type: TabType; index: number }
  ): TabType => {
    return typeof tab === "string" ? tab : tab.type;
  };

  const isTabObject = (
    tab: TabType | { type: TabType; index: number }
  ): tab is { type: TabType; index: number } => {
    return typeof tab === "object" && tab !== null && "index" in tab;
  };

  const processedTabs = sortedTabs
    .map((tab, idx) => {
      const tabType = getTabType(
        tab as TabType | { type: TabType; index: number }
      );
      const tabObj = tab as TabType | { type: TabType; index: number };
      const index = isTabObject(tabObj) ? tabObj.index : idx;
      return { type: tabType, index };
    })
    .sort((a, b) => a.index - b.index);

  const getDefaultTab = (): TabType => {
    return processedTabs[0]?.type;
  };

  const activeTabFromUrl = getActiveTabFromUrl();
  if (
    !activeTabFromUrl &&
    !location.pathname.includes("/registration")
  ) {
    if (processedTabs.length > 0) {
      const defaultTab = getDefaultTab();
      const defaultTabRoute = TabRoutes[defaultTab];
      return <Navigate to={`/events/${id}/${defaultTabRoute}`} replace />;
    }
  }

  const activeTabType = activeTabFromUrl;
  if (
    activeTabType &&
    !processedTabs.some((tab) => tab.type === activeTabType)
  ) {
    if (processedTabs.length > 0) {
      const defaultTab = getDefaultTab();
      const defaultTabRoute = TabRoutes[defaultTab];
      return <Navigate to={`/events/${id}/${defaultTabRoute}`} replace />;
    }
  }

  const activeTabOverride = activeTabFromUrl || undefined;

  return (
    <EventDetailDataProvider
      eventId={event.id!}
      preloadRegistration={preloadRegistration}
    >
      <EventDetailsLayout event={event} activeTabOverride={activeTabOverride}>
        <Outlet />
      </EventDetailsLayout>
    </EventDetailDataProvider>
  );
}
